import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useFetch } from "@/lib/api";
import { fmtDate, gbp } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { PageHeader, Loading, ErrorBox, Section, StatusBadge, DemoTag, Detail } from "@/components/Common";
import { SimpleTable } from "@/components/SimpleTable";
import { ActivityList, NotesPanel } from "@/components/Activity";

export function Customers() {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const { data, loading, error } = useFetch(`/admin/customers?q=${encodeURIComponent(q)}`);
  return (
    <div>
      <PageHeader title="Customers" />
      <Input className="mb-4 max-w-md" placeholder="Search name, email, phone or postcode" aria-label="Search customers" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customers-search-input" />
      <ErrorBox error={error} />
      {loading && !data ? <Loading /> : (
        <SimpleTable testId="customers-table" rows={data || []} onRow={(c) => nav(`/admin/customers/${c.id}`)} rowTestId={(c) => `customer-row-${c.email}`}
          cols={[
            { label: "Name", render: (c) => <span className="font-medium">{c.name}<DemoTag show={c.is_demo} /></span> },
            { label: "Email", render: (c) => c.email },
            { label: "Telephone", render: (c) => c.phone },
            { label: "Postcodes", render: (c) => (c.addresses || []).map((a) => a.postcode).join(", ") || "—" },
            { label: "Requests", render: (c) => c.request_count },
            { label: "Registered", render: (c) => fmtDate(c.created_at) },
          ]} />
      )}
    </div>
  );
}

const Mini = ({ items, to, label }) => (
  !items.length ? <p className="text-sm text-muted-foreground">None.</p> : items.map((x) => (
    <Link key={x.id} to={to(x)} className="mb-2 flex items-center justify-between rounded-md border border-border p-2.5 text-sm hover:bg-muted">
      <span>{label(x)}</span><StatusBadge status={x.status} />
    </Link>))
);

export function CustomerDetail() {
  const { id } = useParams();
  const { data: c, loading, error, reload } = useFetch(`/admin/customers/${id}`);
  if (loading && !c) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  return (
    <div className="space-y-5">
      <PageHeader title={<>{c.name}<DemoTag show={c.is_demo} /></>} subtitle={`Customer since ${fmtDate(c.created_at)}`} />
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Section title="Contact">
            <dl className="grid gap-3 sm:grid-cols-3">
              <Detail label="Email">{c.email}</Detail><Detail label="Telephone">{c.phone}</Detail>
              <Detail label="Properties">{(c.addresses || []).map((a) => <span key={a.id} className="block">{a.address}, {a.postcode}</span>)}</Detail>
            </dl>
          </Section>
          <div className="grid gap-5 md:grid-cols-2">
            <Section title="Job requests"><Mini items={c.requests} to={(x) => `/admin/requests/${x.id}`} label={(x) => `${x.reference} · ${fmtDate(x.created_at)}`} /></Section>
            <Section title="Appointments"><Mini items={c.appointments} to={(x) => `/admin/requests/${x.request_id}`} label={(x) => `${fmtDate(x.date)} ${x.start} · ${x.type}`} /></Section>
            <Section title="Quotations"><Mini items={c.quotations} to={(x) => `/admin/quotations/${x.id}`} label={(x) => `${x.reference} · ${gbp(x.total)}`} /></Section>
            <Section title="Jobs"><Mini items={c.jobs} to={(x) => `/admin/jobs/${x.id}`} label={(x) => x.reference} /></Section>
          </div>
          <Section title="Activity history"><ActivityList items={c.activity} /></Section>
        </div>
        <NotesPanel notes={c.notes} postUrl={`/admin/customers/${id}/notes`} onAdded={reload} />
      </div>
    </div>
  );
}
